@if (selectedLiveClass) {
  <div class="container mx-auto px-0 sm:px-4 py-4 sm:py-6 lg:py-10">
    <div class="max-w-7xl mx-auto">
      <!-- Breadcrumbs Section -->
      <nav class="mb-4 sm:mb-6" aria-label="Breadcrumb">
        <div
          class="daisy-breadcrumbs text-xs sm:text-sm max-w-full overflow-x-auto"
        >
          <ul class="margin-0 flex-nowrap">
            <li class="shrink-0">
              <a
                (click)="navigateToHome()"
                class="inline-flex items-center gap-1.5 sm:gap-2 hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
              >
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  class="h-4 w-4"
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6"
                  />
                </svg>
                My Classes
              </a>
            </li>
            <li class="shrink-0">
              <a
                (click)="navigateToHome()"
                class="hover:text-primary transition-colors cursor-pointer whitespace-nowrap"
              >
                Live Classes
              </a>
            </li>
            <li
              class="text-base-content/60 truncate max-w-[180px] sm:max-w-xs md:max-w-md"
              [title]="selectedLiveClass.title"
            >
              {{ selectedLiveClass.title }}
            </li>
          </ul>
        </div>
      </nav>

      <!-- Header Section -->
      <div class="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-base-300">
        <div class="flex flex-col gap-1.5 sm:gap-2">
          <h1
            class="text-2xl sm:text-3xl lg:text-4xl font-bold text-base-content break-words leading-tight"
          >
            {{ selectedLiveClass.title }}
          </h1>
          <p
            class="text-sm sm:text-base lg:text-lg text-base-content/60 flex items-start sm:items-center gap-2"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="h-5 w-5 shrink-0 mt-0.5 sm:mt-0"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                stroke-width="2"
                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
              />
            </svg>
            <span>See your scheduled events and access class resources</span>
          </p>
        </div>
      </div>

      <!-- Contextual State Banners -->
      @if (isPendingVerification) {
        <div
          class="daisy-alert bg-warning/10 border border-warning/30 text-base-content text-xs p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm rounded-box mb-6"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-10 h-10 rounded-full bg-warning/20 flex items-center justify-center text-warning shrink-0 mt-0.5"
            >
              <i class="fa-solid fa-clock text-base"></i>
            </div>
            <div>
              <h3 class="font-bold text-sm text-base-content">
                Payment Verification in Progress
              </h3>
              <p class="text-xs text-base-content/80 mt-0.5 leading-relaxed">
                Your payment for this live class is currently being reviewed by
                administration. Meeting links and full class resources will
                unlock automatically upon approval.
              </p>
            </div>
          </div>
          <a
            routerLink="/invoices"
            class="daisy-btn daisy-btn-sm daisy-btn-warning gap-1.5 w-full sm:w-auto justify-center shrink-0"
          >
            <i class="fa-solid fa-receipt"></i>
            <span>View Invoices</span>
          </a>
        </div>
      } @else if (singleEventPass) {
        <div
          class="daisy-alert bg-primary/10 border border-primary/20 text-base-content text-xs p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm rounded-box mb-6"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-10 h-10 rounded-full bg-primary/20 flex items-center justify-center text-primary shrink-0 mt-0.5"
            >
              <i class="fa-solid fa-ticket text-base"></i>
            </div>
            <div>
              <div class="flex flex-wrap items-center gap-2">
                <h3 class="font-bold text-sm text-base-content">
                  Enrolled via Single Event Pass
                </h3>
                <span
                  class="daisy-badge daisy-badge-primary daisy-badge-sm font-semibold"
                >
                  Session Pass
                </span>
              </div>
              <p class="text-xs text-base-content/80 mt-0.5 leading-relaxed">
                @if (singleEventPass.targetEvent?.eventDate) {
                  You have access to your purchased session on
                  {{
                    singleEventPass.targetEvent.eventDate | date: 'mediumDate'
                  }}, plus general course resources.
                } @else {
                  You have access to your single session pass, plus general
                  course resources.
                }
                Upgrade anytime to unlock all live sessions, past recordings,
                and tests.
              </p>
            </div>
          </div>
          <a
            [routerLink]="['/live-classes', selectedLiveClassId, 'subscribe']"
            class="daisy-btn daisy-btn-sm daisy-btn-primary gap-1.5 w-full sm:w-auto justify-center shrink-0"
          >
            <i class="fa-solid fa-arrow-up-right-from-square"></i>
            <span>Upgrade to Full Course</span>
          </a>
        </div>
      } @else if (!isAuthorized) {
        <div
          class="daisy-alert bg-base-100 border border-primary/30 text-base-content text-xs p-4 sm:p-5 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 shadow-sm rounded-box mb-6"
        >
          <div class="flex items-start gap-3">
            <div
              class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5"
            >
              <i class="fa-solid fa-graduation-cap text-base"></i>
            </div>
            <div>
              <h3 class="font-bold text-sm text-base-content">
                Enroll to Unlock Full Access
              </h3>
              <p class="text-xs text-base-content/80 mt-0.5 leading-relaxed">
                Subscribe to this live class to attend upcoming interactive
                sessions, access past recordings, and download learning
                materials.
              </p>
            </div>
          </div>
          <a
            [routerLink]="['/live-classes', selectedLiveClassId, 'subscribe']"
            class="daisy-btn daisy-btn-sm daisy-btn-primary gap-1.5 w-full sm:w-auto justify-center shrink-0"
          >
            <i class="fa-solid fa-bolt"></i>
            <span>Subscribe to Course</span>
          </a>
        </div>
      }

      <!-- Class Community Section -->
      @if (
        isAuthorized &&
        (selectedLiveClass.socialLinks?.whatsapp ||
          selectedLiveClass.socialLinks?.telegram)
      ) {
        <div
          class="daisy-card daisy-card-bordered bg-base-100 shadow-sm mb-6 border-primary/20"
        >
          <div
            class="daisy-card-body p-4 sm:p-5 flex flex-col md:flex-row md:items-center justify-between gap-4"
          >
            <div class="flex items-start sm:items-center gap-3">
              <div
                class="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-primary shrink-0 mt-0.5 sm:mt-0"
              >
                <i class="fa-solid fa-users text-lg"></i>
              </div>
              <div class="min-w-0 flex-1">
                <h3
                  class="font-bold text-sm sm:text-base text-base-content flex flex-wrap items-center gap-2"
                >
                  <span>Class Community Groups</span>
                  <span class="daisy-badge daisy-badge-primary daisy-badge-sm"
                    >Enrolled Exclusive</span
                  >
                </h3>
                <p class="text-xs text-base-content/60 mt-0.5">
                  Join official study groups for class announcements, Q&A, and
                  updates from your tutor.
                </p>
              </div>
            </div>
            <div
              class="flex flex-col sm:flex-row items-stretch sm:items-center gap-2.5 w-full md:w-auto"
            >
              @if (selectedLiveClass.socialLinks?.whatsapp) {
                <a
                  [href]="
                    getSocialLink(
                      selectedLiveClass.socialLinks?.whatsapp,
                      'whatsapp'
                    )
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                  class="daisy-btn daisy-btn-sm bg-[#25D366] hover:bg-[#20ba5a] text-white border-none gap-2 w-full sm:w-auto justify-center"
                >
                  <i class="fa-brands fa-whatsapp text-lg"></i>
                  <span>Join WhatsApp Group</span>
                </a>
              }
              @if (selectedLiveClass.socialLinks?.telegram) {
                <a
                  [href]="
                    getSocialLink(
                      selectedLiveClass.socialLinks?.telegram,
                      'telegram'
                    )
                  "
                  target="_blank"
                  rel="noopener noreferrer"
                  class="daisy-btn daisy-btn-sm bg-[#24A1DE] hover:bg-[#208ec5] text-white border-none gap-2 w-full sm:w-auto justify-center"
                >
                  <i class="fa-brands fa-telegram text-lg"></i>
                  <span>Join Telegram Group</span>
                </a>
              }
            </div>
          </div>
        </div>
      }

      <!-- Tabs Section -->

      <div class="w-full">
        <black-ui-tabs [showDefaultTrigger]="true">
          <!-- Sessions Tab -->
          <black-tabs-item class="w-full" name="Sessions">
            <div *blackTabsContent class="py-6">
              <black-meetings-list-view
                [liveClass]="selectedLiveClass"
              ></black-meetings-list-view>
            </div>
          </black-tabs-item>

          <!-- Announcements Tab -->
          <black-tabs-item class="w-full" name="Announcements">
            <div *blackTabsContent class="py-6">
              <black-class-announcements
                [liveClassId]="selectedLiveClass.id || selectedLiveClassId"
                [isInstructor]="isUserInstructor"
              ></black-class-announcements>
            </div>
          </black-tabs-item>

          <!-- Recordings Tab -->
          <black-tabs-item class="w-full" name="Recordings">
            <div *blackTabsContent class="py-6">
              @if (sessionRecordings.length === 0) {
                <div
                  class="flex flex-col items-center justify-center py-16 px-4"
                >
                  <div class="bg-base-200 rounded-full p-6 mb-4">
                    <i
                      class="fa-solid fa-video-camera text-4xl text-base-content/40"
                    ></i>
                  </div>
                  <h3 class="text-xl font-semibold text-base-content mb-2">
                    No Recordings Available
                  </h3>
                  <p class="text-base-content/60 text-center max-w-md">
                    Recordings will appear here once your sessions are completed
                    and processed.
                  </p>
                </div>
              } @else {
                <div class="grid grid-cols-1 gap-4">
                  @for (item of sessionRecordings; track item.sessionId) {
                    <black-recording-card
                      [session]="item"
                      [subscriptions]="selectedLiveClass.subscriptions"
                      [liveClassOwnerId]="selectedLiveClass.user.toString()"
                      [isAuthorized]="isAuthorized"
                      (playVideoEvent)="playVideo($event)"
                      class="transition-transform hover:scale-[1.01]"
                    ></black-recording-card>
                  }
                </div>
              }
            </div>
          </black-tabs-item>

          <!-- Resources Tab -->
          <black-tabs-item name="Resources">
            <div *blackTabsContent class="py-6">
              <black-study-materials
                [selectedLiveClassId]="selectedLiveClassId"
                [selectedLiveClass]="selectedLiveClass"
              ></black-study-materials>
            </div>
          </black-tabs-item>

          <!-- Quizzes Tab -->
          <black-tabs-item name="Quizzes">
            <div *blackTabsContent class="py-6">
              <black-student-quizzes
                [liveClassId]="selectedLiveClassId"
                [isAuthorized]="isAuthorized"
                [isPending]="isPendingVerification"
              ></black-student-quizzes>
            </div>
          </black-tabs-item>
        </black-ui-tabs>
      </div>
    </div>
  </div>
} @else {
  <!-- Loading Skeleton -->
  <div class="container mx-auto px-0 sm:px-4 py-4 sm:py-6 lg:py-10">
    <div class="max-w-7xl mx-auto">
      <!-- Breadcrumbs Skeleton -->
      <div class="mb-4 sm:mb-6">
        <div class="daisy-skeleton h-4 sm:h-5 w-48 sm:w-64 rounded"></div>
      </div>

      <!-- Header Skeleton -->
      <div class="mb-6 sm:mb-8 pb-4 sm:pb-6 border-b border-base-300">
        <div class="space-y-3">
          <div class="daisy-skeleton h-8 sm:h-10 w-3/4 lg:w-1/2 rounded"></div>
          <div class="daisy-skeleton h-5 sm:h-6 w-full lg:w-2/3 rounded"></div>
        </div>
      </div>

      <!-- Tabs Skeleton -->
      <div class="space-y-6">
        <div
          class="flex gap-2 sm:gap-4 border-b border-base-300 overflow-x-auto"
        >
          <div
            class="daisy-skeleton h-9 sm:h-10 w-20 sm:w-24 rounded-t shrink-0"
          ></div>
          <div
            class="daisy-skeleton h-9 sm:h-10 w-24 sm:w-28 rounded-t shrink-0"
          ></div>
          <div
            class="daisy-skeleton h-9 sm:h-10 w-20 sm:w-24 rounded-t shrink-0"
          ></div>
          <div
            class="daisy-skeleton h-9 sm:h-10 w-20 sm:w-24 rounded-t shrink-0"
          ></div>
        </div>

        <!-- Content Skeleton -->
        <div class="space-y-4 py-6">
          <div class="daisy-skeleton h-32 w-full rounded-lg"></div>
          <div class="daisy-skeleton h-32 w-full rounded-lg"></div>
          <div class="daisy-skeleton h-32 w-full rounded-lg"></div>
        </div>
      </div>
    </div>
  </div>
}
